import { useState } from "react";
import { FaFacebookF } from "react-icons/fa";
import { HiPlus } from "react-icons/hi2";
import { IoCloseOutline } from "react-icons/io5";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { PiInstagramLogo, PiYoutubeLogoFill } from "react-icons/pi";
import styled from "styled-components";
import currencies from "../../../../assets/store/currencies";
import colors from "../../../../theme/color/ColorTheme";
import fontSize from "../../../../theme/fonts/FontTheme";
import { appear } from "../../../animations/Appear";
import { moveInLeft } from "../../../animations/MoveInLeft";
import ClothingMegaMenu from "../../../megaMenu/mobile/ClothingMegaMenuMobile";

/* ===================== STYLES ===================== */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: #00000045;
  z-index: 98;
`;

const Wrapper = styled.div`
  height: 100dvh;
  width: 90vw;
  max-width: 450px;
  position: fixed;
  top: 0;
  left: 0;
  background-color: ${colors.white};
  color: ${colors.black};
  z-index: 99;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  animation: ${moveInLeft} 0.2s ease-in-out;
`;

const NavContainer = styled.div`
  padding: 20px 10px;
`;

const Close = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 5px;
  svg {
    color: ${colors.primaryGray};
    height: 24px;
    width: 24px;
  }
  cursor: pointer;
  &:hover {
    span {
      display: initial;
    }
  }
  span {
    font-size: ${fontSize.xs};
    display: none;
    animation: ${appear} 0.1s ease-in;
  }
`;

const NavList = styled.ul`
  padding: 40px 10px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  font-size: ${fontSize.xxs};
`;

const NavItem = styled.li`
  list-style: none;
  min-height: 40px;
  border-bottom: 0.2px solid ${colors.primaryGray};

  span {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    svg {
      color: ${colors.black};
      height: 16px;
      width: 16px;
    }
  }
`;

const AccordionContent = styled.div`
  max-height: ${({ $open }) => ($open ? "1800px" : "0px")};
  overflow: hidden;
  transition: max-height 0.8s ease;
`;

const PlusIcon = styled(HiPlus)`
  transition: transform 0.3s ease;
  transform: ${({ $open }) => ($open ? "rotate(45deg)" : "rotate(0deg)")};
`;

const NavFooter = styled.div`
  height: 50px;
  border-top: 0.5px solid ${colors.primaryGray};
  margin-top: auto;
  display: flex;
`;

const FooterItem = styled.div`
  width: 25%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: ${colors.primaryGray};
    height: 20px;
    width: 20px;

    &:hover {
      color: ${colors.black};
    }
  }
`;

const CurrencySelectWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-right: 0.5px solid ${colors.primaryGray};
`;

const CurrencySelect = styled.select`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
  padding-right: 40px;
  font-size: ${fontSize.xxs};
  letter-spacing: 0.08rem;
  cursor: pointer;
  outline: none;
  direction: rtl;
  color: ${colors.primaryGray};
  appearance: none;
`;

const Icon = styled(MdOutlineKeyboardArrowDown)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

/* ===================== DATA ===================== */

const MENU_ITEMS = [
  { name: "SCARVES", url: "/browse?category=shawl" },
  { name: "HOME", url: "/" },
  {
    name: "CLOTHING",
    accordionContent: <ClothingMegaMenu />,
  },
  { name: "LANGTANG COLLECTION - COMING SOON", url: "/browse" },
  { name: "JOURNAL", url: "/journal" },
  { name: "ABOUT US", url: "/founder" },
  { name: "CONTACT", url: "/contact" },
];

/* ===================== COMPONENT ===================== */

function MobileNavDrawer({ onClose }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(currencies[0].abbr);

  const toggleAccordion = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <>
      <Overlay
        onClick={() => {
          onClose();
        }}
      />

      <Wrapper>
        <NavContainer>
          <Close onClick={onClose}>
            <IoCloseOutline />
            <span>(Close)</span>
          </Close>

          <NavList>
            {MENU_ITEMS.map((item, index) => {
              const isOpen = openIndex === index;
              const hasAccordion = !!item.accordionContent;

              return (
                <NavItem
                  key={item.name}
                  onClick={() =>
                    hasAccordion
                      ? toggleAccordion(index)
                      : (window.location.href = item.url)
                  }
                >
                  <span>
                    {item.name}
                    {hasAccordion && <PlusIcon $open={isOpen} />}
                  </span>

                  {hasAccordion && (
                    <AccordionContent $open={isOpen}>
                      {item.accordionContent}
                    </AccordionContent>
                  )}
                </NavItem>
              );
            })}
          </NavList>
        </NavContainer>

        <NavFooter>
          <FooterItem>
            <CurrencySelectWrapper>
              <CurrencySelect
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
              >
                {currencies.map((c) => (
                  <option key={c.abbr} value={c.abbr}>
                    {c.abbr}
                  </option>
                ))}
              </CurrencySelect>
              <Icon />
            </CurrencySelectWrapper>
          </FooterItem>

          <FooterItem>
            <FaFacebookF />
          </FooterItem>
          <FooterItem>
            <PiInstagramLogo />
          </FooterItem>
          <FooterItem>
            <PiYoutubeLogoFill />
          </FooterItem>
        </NavFooter>
      </Wrapper>
    </>
  );
}

export default MobileNavDrawer;
